/**
 * features/parent/ParentFinance.tsx
 * ------------------------------------------------------------------
 * Parent portal — Fees tab. A read-only, combined view of the family
 * account: every linked child's invoices and payments folded into one
 * balance, with a downloadable statement. Data is scoped to the
 * parent's own children (queried by their linked studentIds; the rules
 * enforce that a parent can only read their own children's records).
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { Wallet, FileDown, ReceiptText, RefreshCw } from 'lucide-react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { GradientHero, SoftCard, StatChip, AccentButton } from '@/features/finance/ui';
import { useParentPortalControls, ParentNoticeBanner, ParentMaintenanceScreen } from '@/features/parent/parentPortalControls';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildStatementPdf } from '@/lib/statementPdf';
import StudentFeesPanel from '@/features/finance/StudentFeesPanel';
import {
  buildFamilyStatement, fmtPula, fmtBalance, DEFAULT_FINANCE_SETTINGS,
  type StatementAdjustment,
  type Invoice, type Payment, type FamilyStatement,
} from '@/lib/financeCore';

export default function ParentFinance() {
  const { userData } = useAuth();
  const { students, familyById } = useData();

  const studentIds = useMemo(() => userData?.parentAccount?.studentIds ?? [], [userData]);
  const idKey = studentIds.join(',');

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  /* Credit notes and write-offs. Without them the portal can see that money
     came OFF the account but not what it was, so a sibling discount reaches
     the family as an unnamed "adjustment". */
  const [adjustments, setAdjustments] = useState<StatementAdjustment[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (studentIds.length === 0) { setReady(true); return; }
    const ids = studentIds.slice(0, 30);
    let gotI = false; let gotP = false;
    /* Deliberately NOT part of `settle`. The balance is built from the
       invoices; the adjustments only put a name to a reduction already in
       it. Holding the whole screen until they arrive would let a rules
       change turn a naming gap into a blank fees page. */
    const settle = () => { if (gotI && gotP) setReady(true); };
    const ui = onSnapshot(query(collection(db, 'financeInvoices'), where('studentId', 'in', ids)), (snap) => {
      setInvoices(snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) } as Invoice))); gotI = true; settle();
    }, () => { gotI = true; settle(); });
    const up = onSnapshot(query(collection(db, 'financePayments'), where('studentId', 'in', ids)), (snap) => {
      setPayments(snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) } as Payment))); gotP = true; settle();
    }, () => { gotP = true; settle(); });
    const ua = onSnapshot(query(collection(db, 'financeAdjustments'), where('studentId', 'in', ids)), (snap) => {
      setAdjustments(snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) } as StatementAdjustment)));
    }, () => setAdjustments([]));
    return () => { ui(); up(); ua(); };
  }, [idKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const linked = useMemo(() => students.filter((s) => studentIds.includes(s.id)), [students, idKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const familyId = linked[0]?.familyId ?? '';
  const fam = familyId ? familyById(familyId) : undefined;

  const statement: FamilyStatement = useMemo(() => buildFamilyStatement({
    familyId: familyId || 'account',
    familyName: fam?.name ?? 'Your account',
    contactName: fam?.primaryContact?.name,
    contactEmail: fam?.primaryContact?.email ?? userData?.email ?? undefined,
    members: linked.map((s) => ({ studentId: s.id, name: s.fullName })),
    invoices, payments, adjustments, audience: 'parent',
    settings: DEFAULT_FINANCE_SETTINGS,
  }), [familyId, fam, linked, invoices, payments, adjustments, userData]);

  const portal = useParentPortalControls();

  // Maintenance / balances-hidden gates (display-only; the arrears hold is
  // computed elsewhere and is unaffected by these).
  if (portal.maintenance.enabled) {
    return <ParentMaintenanceScreen message={portal.maintenance.message} />;
  }
  if (!portal.feesVisible) {
    return (
      <div className="space-y-5">
        <GradientHero icon={Wallet} title="Fees" subtitle="Your family account" />
        {portal.notice.enabled && <ParentNoticeBanner message={portal.notice.message} />}
        <SoftCard>
          <div className="flex items-start gap-3">
            <RefreshCw size={20} className="text-[var(--accent-link,#0d9488)] mt-0.5 shrink-0" />
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <p className="font-medium text-gray-800 dark:text-gray-100">Fee balances are being updated.</p>
              <p className="mt-1">We're bringing your account fully up to date. Balances will be available again shortly — please check back in 24 hours. If you have a question in the meantime, contact the school bursary.</p>
            </div>
          </div>
        </SoftCard>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="space-y-6">
        <GradientHero icon={Wallet} title="Fees" subtitle="Your family account — combined balance and statement" />
        <div className="flex justify-center py-12"><Spinner label="Loading your account…" /></div>
      </div>
    );
  }

  if (statement.txns.length === 0) {
    return (
      <div className="space-y-6">
        <GradientHero icon={Wallet} title="Fees" subtitle="Your family account — combined balance and statement" />
        <SoftCard>
          <div className="flex items-start gap-3">
            <ReceiptText size={20} className="text-[var(--accent-link,#0d9488)] mt-0.5 shrink-0" />
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <p className="font-medium text-gray-800 dark:text-gray-100">No fees on your account yet.</p>
              <p className="mt-1">When the school issues invoices for your {linked.length > 1 ? 'children' : 'child'}, they'll appear here with a combined balance and a downloadable statement.</p>
            </div>
          </div>
        </SoftCard>
      </div>
    );
  }

  const download = async () => {
    const logo = await loadLogoDataUrl();
    buildStatementPdf(statement, logo).save(`statement_${statement.familyName.replace(/\s+/g, '_')}.pdf`);
  };

  return (
    <div className="space-y-5">
      <GradientHero icon={Wallet} title="Fees" subtitle="Your family account — combined balance and statement" />
      {portal.notice.enabled && <ParentNoticeBanner message={portal.notice.message} />}
      <SoftCard>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Wallet size={18} className="text-[var(--accent-link,#0d9488)]" />
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">Family account</h3>
          </div>
          <AccentButton icon={FileDown} onClick={download}>Download statement</AccentButton>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <StatChip tone="blue" value={fmtPula(statement.billed)} label="Billed" />
          <StatChip tone="emerald" value={fmtPula(statement.paid)} label="Paid" />
          <StatChip tone={statement.balance > 0 ? 'red' : 'emerald'} value={fmtBalance(statement.balance)} label="Balance due" />
        </div>
      </SoftCard>

      {statement.members.length > 1 && (
        <SoftCard>
          <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3">By child</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 pr-3 font-medium">Student</th>
                <th className="py-2 px-3 font-medium text-right">Billed</th>
                <th className="py-2 px-3 font-medium text-right">Paid</th>
                <th className="py-2 pl-3 font-medium text-right">Balance</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {statement.members.map((m) => (
                  <tr key={m.studentId}>
                    <td className="py-2 pr-3 text-gray-800 dark:text-gray-100">{m.name}</td>
                    <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(m.billed)}</td>
                    <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(m.paid)}</td>
                    <td className="py-2 pl-3 text-right font-medium text-gray-800 dark:text-gray-100">{fmtBalance(m.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SoftCard>
      )}

      <SoftCard>
        <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3">Account activity</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
              <th className="py-2 pr-3 font-medium">Date</th>
              <th className="py-2 px-3 font-medium">Description</th>
              <th className="py-2 px-3 font-medium text-right">Charge</th>
              <th className="py-2 px-3 font-medium text-right">Credit</th>
              <th className="py-2 pl-3 font-medium text-right">Balance</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {statement.txns.map((t, idx) => (
                <tr key={idx}>
                  <td className="py-2 pr-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{(t.date || '').slice(0, 10)}</td>
                  <td className="py-2 px-3 text-gray-800 dark:text-gray-100">{t.description}</td>
                  <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{t.charge ? fmtPula(t.charge) : ''}</td>
                  <td className="py-2 px-3 text-right text-emerald-600 dark:text-emerald-400">{t.credit ? fmtPula(t.credit) : ''}</td>
                  <td className="py-2 pl-3 text-right font-medium text-gray-800 dark:text-gray-100">{fmtBalance(t.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {statement.balance > 0 && (
          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Payment is by bank transfer (EFT) or card. Please quote <span className="font-medium">{statement.familyName}</span> as the reference.
          </p>
        )}
      </SoftCard>

      {linked.map((child) => (
        <SoftCard key={child.id}>
          <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3">{child.fullName}</h4>
          <StudentFeesPanel
            studentName={child.fullName}
            studentId={child.id}
            audience="parent"
            invoices={invoices.filter((i) => i.studentId === child.id)}
            payments={payments.filter((p) => p.studentId === child.id)}
          />
        </SoftCard>
      ))}
    </div>
  );
}
